import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import SearchPicker, { PickerOption } from './SearchPicker';
import pettyCashApi from '../../services/pettyCashApi';
import type { CodeName, Dims, FinanceLineChange, LineType, PettyCashConfig } from '../../types/pettyCash';
import type { ScreenLine } from './LinesTable';
import { ruleSummary } from './AccountSourceChip';
import { formatAmount } from '../../utils/pettyCash';

/**
 * Finance's changes to one receipt: the G/L account (empty = SAP's Advanced G/L rule, else the category's
 * fixed account), the VAT code, the dimension codes, VAT or non-VAT, and disallowing it (not posted; the
 * holder is told). In the finance review before the posting, and after a posting failure for a receipt SAP
 * refused ("Correct receipt N"), before posting it again.
 */

interface Props {
  open: boolean;
  companyId: number;
  config: PettyCashConfig;
  line: ScreenLine | null;
  /** The claim is partly posted: correcting a receipt SAP refused. */
  correcting?: boolean;
  onClose: () => void;
  onSave: (change: FinanceLineChange) => Promise<void>;
}

const FinanceLineDialog: React.FC<Props> = ({ open, companyId, config, line, correcting, onClose, onSave }) => {
  const { vatCodes, settings, dimensions, company } = config;
  const [account, setAccount] = useState<PickerOption | null>(null);
  const [vatCode, setVatCode] = useState('');
  const [lineType, setLineType] = useState<LineType>('VAT');
  const [dims, setDims] = useState<Dims>({});
  const [disallow, setDisallow] = useState(false);
  const [reason, setReason] = useState('');
  const [codes, setCodes] = useState<Record<string, CodeName[]>>({});
  // The auxiliaries (Dimension 5) of the line account's type, when the account takes them.
  const [auxCodes, setAuxCodes] = useState<CodeName[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !line) return;
    setAccount(line.glAccount ? { code: line.glAccount, name: line.glAccount === line.effectiveAccount ? line.effectiveAccountName || '' : '' } : null);
    setVatCode(line.vatCode || '');
    setLineType(line.lineType);
    setDims({ ...(line.dimsOverride || {}) });
    setDisallow(line.status === 'Disallowed');
    setReason(line.disallowReason || '');
    setError(null);
    setBusy(false);
  }, [open, line]);

  const activeDims = dimensions.filter((d) => d.active);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const missing = activeDims.filter((d) => !codes[String(d.code)]);
    Promise.all(missing.map((d) => pettyCashApi.dimensionCodes(companyId, d.code).catch(() => [] as CodeName[])))
      .then((lists) => {
        if (cancelled) return;
        setCodes((c) => {
          const next = { ...c };
          missing.forEach((d, i) => { next[String(d.code)] = lists[i]; });
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const auxType = line?.accountAux?.type || null;
  useEffect(() => {
    if (!open || !auxType) { setAuxCodes(null); return undefined; }
    let cancelled = false;
    pettyCashApi.auxiliaries(companyId, { type: auxType })
      .then((list) => { if (!cancelled) setAuxCodes(list.codes.map((c) => ({ code: c.code, name: c.name }))); })
      .catch(() => { if (!cancelled) setAuxCodes(null); });
    return () => { cancelled = true; };
  }, [open, companyId, auxType]);

  const searchAccounts = useCallback(async (q: string): Promise<PickerOption[]> => {
    const rows = await pettyCashApi.searchAccounts(companyId, q);
    return rows.map((r) => ({ code: r.code, name: r.name }));
  }, [companyId]);

  if (!line) return null;

  const save = async () => {
    if (disallow && !reason.trim()) { setError('Give the reason for disallowing the receipt: the holder sees it.'); return; }
    const change: FinanceLineChange = {};
    const glAccount = account?.code || null;
    if (glAccount !== (line.glAccount || null)) change.glAccount = glAccount;
    if (lineType !== line.lineType) change.lineType = lineType;
    if (lineType === 'VAT' && vatCode && vatCode !== line.vatCode) change.vatCode = vatCode;
    const cleaned: Dims = {};
    for (const [k, v] of Object.entries(dims)) if (v) cleaned[k] = v;
    const before = JSON.stringify(line.dimsOverride || {});
    if (JSON.stringify(cleaned) !== before) change.dimsOverride = Object.keys(cleaned).length ? cleaned : null;
    if (disallow && (line.status !== 'Disallowed' || reason.trim() !== (line.disallowReason || ''))) change.disallowReason = reason.trim();
    if (!disallow && line.status === 'Disallowed') change.disallowReason = null;
    if (!Object.keys(change).length) { onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(change);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The change could not be saved');
    } finally {
      setBusy(false);
    }
  };

  // What an empty account field means: SAP's rule for this receipt, else the category's fixed account.
  const ruleText = line.ruleAccount
    ? `SAP's G/L rules give ${line.ruleAccount}${line.ruleAccountName ? ` ${line.ruleAccountName}` : ''}${line.accountRule ? ` (${ruleSummary(line.accountRule)})` : ''}`
    : line.categoryAccount
      ? `No SAP rule gives an account: the category's ${line.categoryAccount}${line.categoryAccountName ? ` ${line.categoryAccountName}` : ''} applies`
      : line.ruleAccount === null && line.categoryAccount === null
        ? 'Neither SAP\'s G/L rules nor the category give an account: choose one'
        : '';
  const emptyMeans = line.ruleAccount
    ? `Leave empty to use SAP's rule (${line.ruleAccount})`
    : line.categoryAccount ? `Leave empty to use the category's account (${line.categoryAccount})` : 'Leave empty to use SAP\'s rule or the category\'s account';

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="finance-line-title">
      <DialogTitle id="finance-line-title">{correcting ? 'Correct receipt' : 'Receipt'} {line.lineNum}: {line.supplierName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {correcting && line.postingError ? (
            <Alert severity="error">
              <strong>SAP refused this receipt:</strong> {line.postingError}
              <Typography variant="body2" sx={{ mt: 0.5 }}>Correct it here, then press Post again on the claim. Receipts already in SAP are not posted twice.</Typography>
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {line.invoiceNo} · total {formatAmount(line.total)} {company.currency} · now posts to {line.effectiveAccount || 'no account'} {line.effectiveAccountName || ''}
          </Typography>
          {ruleText ? <Typography variant="body2" color="text.secondary">{ruleText}</Typography> : null}
          {company.vatRegistered ? (
            <RadioGroup row value={lineType} onChange={(e) => setLineType(e.target.value as LineType)} aria-label="Receipt type">
              {/* The server only turns a VAT receipt into a non-VAT one, never back. */}
              <FormControlLabel value="VAT" control={<Radio />} label="VAT receipt" disabled={line.lineType === 'NonVAT'} />
              <FormControlLabel value="NonVAT" control={<Radio />} label={`Non-VAT receipt (posts to ${settings.cashSupplier || 'the cash supplier'}, VAT kept in the expense)`} />
            </RadioGroup>
          ) : null}
          {company.vatRegistered && line.lineType === 'NonVAT' ? (
            <Typography variant="caption" color="text.secondary">A non-VAT receipt has no SAP vendor: to post it as a VAT receipt, return the claim so the holder can find the VAT supplier.</Typography>
          ) : null}
          <SearchPicker
            id="fin-account"
            label="G/L account"
            value={account}
            onChange={setAccount}
            search={searchAccounts}
            helperText={emptyMeans}
          />
          {lineType === 'VAT' ? (
            <TextField id="fin-vatcode" select label="VAT code" value={vatCode} onChange={(e) => setVatCode(e.target.value)} helperText={`Codes at the receipt's ${line.vatRate}%; for another rate, return the claim`} fullWidth>
              {/* The server refuses a code of another rate (the VAT amount would no longer match). */}
              {vatCodes.filter((c) => c.code === line.vatCode || (c.code !== settings.nonVatCode && Math.abs(c.rate - line.vatRate) < 0.0001)).map((c) => <MenuItem key={c.code} value={c.code}>{c.code} · {c.name} ({c.rate}%)</MenuItem>)}
            </TextField>
          ) : null}
          {activeDims.length ? (
            <Grid container spacing={2}>
              {activeDims.map((d) => {
                const key = String(d.code);
                // Dimension 5 holds the auxiliaries: offer the account's type only when it has one.
                const auxDim = key === '5' && !!line.accountAux;
                const options = auxDim && auxCodes ? auxCodes : codes[key] || [];
                const current = line.dims?.[key] || '';
                const needed = auxDim && !!line.accountAux?.mandatory && !current && !dims[key];
                const auxHint = auxDim ? `Auxiliary ${line.accountAux?.type || ''}${line.accountAux?.mandatory ? ', required by the account' : ''}` : '';
                return (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      id={`fin-dim-${key}`}
                      select
                      label={d.label}
                      value={dims[key] || ''}
                      onChange={(e) => setDims((x) => ({ ...x, [key]: e.target.value }))}
                      helperText={[auxHint, current ? `Now ${current}` : ''].filter(Boolean).join(' · ') || undefined}
                      error={needed}
                      fullWidth
                    >
                      <MenuItem value="">As on the claim</MenuItem>
                      {options.map((o) => <MenuItem key={o.code} value={o.code}>{o.code} {o.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                );
              })}
            </Grid>
          ) : null}
          <FormControlLabel control={<Checkbox checked={disallow} onChange={(e) => setDisallow(e.target.checked)} />} label="Disallow this receipt (it is not posted)" />
          {disallow ? <TextField id="fin-reason" label="Reason (the holder sees it)" value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} multiline minRows={2} fullWidth required /> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default FinanceLineDialog;
